// src/pages/admin/components/sections/LocationSectionEditor.jsx
import React, { useEffect, useMemo, useState } from "react";
import Field from "./shared/Field";
import HeroImageUploader from "./shared/HeroImageUploader";
import EditorGroup from "./shared/EditorGroup";
import Segmented from "./shared/Segmented";

function normalizeLocationTemplate(tpl) {
  const t = String(tpl ?? "").toLowerCase().trim();
  if (t === "1" || t === "template1" || t.endsWith("template1")) return "template1";
  if (t === "2" || t === "template2" || t.endsWith("template2")) return "template2";
  if (t === "3" || t === "template3" || t.endsWith("template3")) return "template3";
  return "template1";
}

const DAYS = [
  ["monday", "Monday"],
  ["tuesday", "Tuesday"],
  ["wednesday", "Wednesday"],
  ["thursday", "Thursday"],
  ["friday", "Friday"],
  ["saturday", "Saturday"],
  ["sunday", "Sunday"],
];

const makeHours = () =>
  DAYS.reduce((acc, [k]) => {
    acc[k] = "";
    return acc;
  }, {});

const makeNewBranchV2 = () => ({
  id: Date.now(),
  name: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  phone: "",
  email: "",
  manager: "",
  image: "",
  mapLink: "",
  mapEmbedUrl: "",
  coordinates: { lat: "", lng: "" },
  features: [],
  hours: makeHours(),
});

const makeNewLocationV3 = () => ({
  id: Date.now(),
  name: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  phone: "",
  email: "",
  hours: "",
  image: "",
  mapLink: "",
  mapEmbedUrl: "",
  coordinates: { lat: "", lng: "" },
});

function ChipsInput({
  label,
  value,
  onChange,
  placeholder,
  help,
  t = (k, f) => f,
}) {
  const [draft, setDraft] = useState("");
  const items = Array.isArray(value) ? value : [];

  const addItem = (raw) => {
    const v = String(raw || "").trim();
    if (!v) return;
    if (items.includes(v)) return;
    onChange?.([...items, v]);
    setDraft("");
  };

  const removeItem = (v) => onChange?.(items.filter((x) => x !== v));

  return (
    <div className="space-y-2">
      {label ? <div className="text-sm font-medium text-gray-700">{label}</div> : null}
      {help ? <div className="text-xs text-gray-500">{help}</div> : null}

      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addItem(draft);
            }
          }}
          placeholder={placeholder || ""}
          className="flex-1 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-200"
        />
        <button
          type="button"
          onClick={() => addItem(draft)}
          className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm hover:bg-gray-50"
        >
          {t("locationEditor.common.add", "Add")}
        </button>
      </div>

      {items.length ? (
        <div className="flex flex-wrap gap-2 pt-1">
          {items.map((v) => (
            <span
              key={v}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 border border-gray-200 text-sm"
            >
              <span className="truncate max-w-[220px]">{v}</span>
              <button
                type="button"
                onClick={() => removeItem(v)}
                className="text-gray-500 hover:text-gray-800"
                aria-label={t("locationEditor.common.remove", "Remove")}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-xs text-gray-500">
          {t("locationEditor.common.noItems", "No items yet.")}
        </div>
      )}
    </div>
  );
}

function SelectCards({
  items,
  activeId,
  getTitle,
  getSubtitle,
  onSelect,
  onAdd,
  onRemove,
  addLabel,
  t = (k, f) => f,
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {items.map((it, idx) => {
          const id = it?.id ?? `idx-${idx}`;
          const active = String(activeId) === String(id);

          return (
            <button
              key={id}
              type="button"
              onClick={() => onSelect(id)}
              className={`relative text-left rounded-xl border px-3 py-2 min-w-[160px] transition-all ${
                active
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 bg-white hover:bg-gray-50"
              }`}
            >
              <div className="text-sm font-semibold text-gray-900 truncate">
                {getTitle(it, idx)}
              </div>
              <div className="text-xs text-gray-500 truncate mt-0.5">
                {getSubtitle(it, idx)}
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove(idx);
                }}
                className="absolute top-1.5 right-1.5 px-2 py-1 text-xs rounded-md border border-gray-200 bg-white hover:bg-gray-50 text-red-600"
              >
                {t("locationEditor.common.remove", "Remove")}
              </button>
            </button>
          );
        })}

        <button
          type="button"
          onClick={onAdd}
          className="rounded-xl border border-dashed border-gray-300 px-4 py-2 bg-white hover:bg-gray-50 text-sm font-medium"
        >
          {addLabel}
        </button>
      </div>

      {items.length ? (
        <div className="text-xs text-gray-500">
          {t(
            "locationEditor.common.selectTip",
            "Tip: select one card to edit. Only one location is shown below to keep it simple."
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function LocationSectionEditor({
  config,
  template,
  onChange,
  uploadImage,
  t = (k, f) => f,
}) {
  const cfg = config || {};
  const tplKey = useMemo(() => normalizeLocationTemplate(template), [template]);
  const setField = (key, value) => onChange?.(key, value);

  const locations = Array.isArray(cfg.locations) ? cfg.locations : [];

  useEffect(() => {
    if (tplKey !== "template2" && tplKey !== "template3") return;
    if (!Array.isArray(cfg.locations)) return;

    const needsId = cfg.locations.some((l) => !l?.id);
    if (!needsId) return;

    const base = Date.now();
    const next = cfg.locations.map((l, i) => ({ id: l?.id ?? base + i, ...l }));
    setField("locations", next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplKey]);

  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    if (tplKey !== "template2" && tplKey !== "template3") return;

    const firstId = locations[0]?.id ?? null;
    if (!activeId && firstId) setActiveId(firstId);

    const stillExists = locations.some((l) => String(l?.id) === String(activeId));
    if (activeId && !stillExists) setActiveId(firstId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplKey, locations.length]);

  const updateLoc = (index, patch) => {
    const next = [...locations];
    next[index] = { ...(next[index] || {}), ...patch };
    setField("locations", next);
  };

  const removeLoc = (index) => {
    const next = [...locations];
    next.splice(index, 1);
    setField("locations", next);
  };

  const addLoc = () => {
    if (tplKey === "template2") {
      const next = [...locations, makeNewBranchV2()];
      setField("locations", next);
      setActiveId(next[next.length - 1]?.id);
    }
    if (tplKey === "template3") {
      const next = [...locations, makeNewLocationV3()];
      setField("locations", next);
      setActiveId(next[next.length - 1]?.id);
    }
  };

  const activeIndex = locations.findIndex((l) => String(l?.id) === String(activeId));
  const activeLoc = activeIndex >= 0 ? locations[activeIndex] : null;

  return (
    <div className="space-y-4">
      <EditorGroup
        title={t("locationEditor.header.title", "Header")}
        subtitle={t(
          "locationEditor.header.help",
          "Title, subtitle and direction for this section"
        )}
        toggleLabel={t("locationEditor.header.toggle", "Toggle")}
        defaultOpen
      >
        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("locationEditor.header.layoutDirection", "Layout Direction")}
            </div>
            <Segmented
              value={cfg.direction || "ltr"}
              onChange={(v) => setField("direction", v)}
              options={[
                { value: "ltr", label: t("locationEditor.header.ltr", "LTR") },
                { value: "rtl", label: t("locationEditor.header.rtl", "RTL") },
              ]}
            />
            <div className="text-xs text-gray-500 mt-2">
              {t(
                "locationEditor.header.directionHelp",
                "RTL is best for Arabic and Hebrew"
              )}
            </div>
          </div>

          <Field
            label={t("locationEditor.header.fields.title", "Title")}
            value={cfg.title || ""}
            onChange={(v) => setField("title", v)}
            placeholder={t("locationEditor.header.fields.titlePh", "Our Locations")}
          />
          <Field
            label={t("locationEditor.header.fields.subtitle", "Subtitle")}
            type="textarea"
            value={cfg.subtitle || ""}
            onChange={(v) => setField("subtitle", v)}
            placeholder={t(
              "locationEditor.header.fields.subtitlePh",
              "Find the nearest branch and get directions"
            )}
          />
        </div>
      </EditorGroup>

      {tplKey === "template1" ? (
        <EditorGroup
          title={t("locationEditor.t1.title", "Single Location")}
          subtitle={t(
            "locationEditor.t1.help",
            "Use this when you only have one address"
          )}
          toggleLabel={t("locationEditor.t1.toggle", "Toggle")}
          defaultOpen
        >
          <div className="space-y-4">
            <EditorGroup
              title={t("locationEditor.t1.basicTitle", "Basic info")}
              subtitle={t(
                "locationEditor.t1.basicHelp",
                "Business name and opening hours"
              )}
              toggleLabel={t("locationEditor.t1.basicToggle", "Toggle")}
              defaultOpen
            >
              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label={t("locationEditor.t1.businessName", "Business Name")}
                  value={cfg.businessName || ""}
                  onChange={(v) => setField("businessName", v)}
                />
                <Field
                  label={t("locationEditor.t1.businessHours", "Business Hours")}
                  value={cfg.hours || ""}
                  onChange={(v) => setField("hours", v)}
                  placeholder={t(
                    "locationEditor.t1.businessHoursPlaceholder",
                    "Mon-Fri: 9am-6pm, Sat: 10am-4pm"
                  )}
                />
              </div>
            </EditorGroup>

            <EditorGroup
              title={t("locationEditor.common.contact", "Contact")}
              subtitle={t(
                "locationEditor.common.contactHelp",
                "Phone and email shown to customers"
              )}
              toggleLabel={t("locationEditor.common.contactToggle", "Toggle")}
              defaultOpen={false}
            >
              <div className="grid md:grid-cols-2 gap-4">
                <Field
                  label={t("locationEditor.common.phone", "Phone")}
                  value={cfg.phone || ""}
                  onChange={(v) => setField("phone", v)}
                />
                <Field
                  label={t("locationEditor.common.email", "Email")}
                  value={cfg.email || ""}
                  onChange={(v) => setField("email", v)}
                />
              </div>
            </EditorGroup>

            <EditorGroup
              title={t("locationEditor.common.addressTitle", "Address")}
              subtitle={t(
                "locationEditor.common.addressHelp",
                "Full address used for display"
              )}
              toggleLabel={t("locationEditor.common.addressToggle", "Toggle")}
              defaultOpen
            >
              <div className="space-y-4">
                <Field
                  label={t("locationEditor.common.address", "Address")}
                  value={cfg.address || ""}
                  onChange={(v) => setField("address", v)}
                />
                <div className="grid md:grid-cols-3 gap-4">
                  <Field
                    label={t("locationEditor.common.city", "City")}
                    value={cfg.city || ""}
                    onChange={(v) => setField("city", v)}
                  />
                  <Field
                    label={t("locationEditor.common.state", "State")}
                    value={cfg.state || ""}
                    onChange={(v) => setField("state", v)}
                  />
                  <Field
                    label={t("locationEditor.common.zip", "ZIP")}
                    value={cfg.zip || ""}
                    onChange={(v) => setField("zip", v)}
                  />
                </div>
                <Field
                  label={t("locationEditor.t1.country", "Country")}
                  value={cfg.country || ""}
                  onChange={(v) => setField("country", v)}
                />
              </div>
            </EditorGroup>

            <EditorGroup
              title={t("locationEditor.t1.mapTitle", "Map")}
              subtitle={t(
                "locationEditor.t1.mapHelp",
                "Embed URL and optional directions link"
              )}
              toggleLabel={t("locationEditor.t1.mapToggle", "Toggle")}
              defaultOpen={false}
            >
              <Field
                label={t("locationEditor.t1.mapEmbedUrl", "Google Maps Embed URL")}
                type="textarea"
                value={cfg.mapEmbedUrl || ""}
                onChange={(v) => setField("mapEmbedUrl", v)}
              />
              <Field
                label={t(
                  "locationEditor.common.directionsLinkOptional",
                  "Directions Link (optional)"
                )}
                value={cfg.directionLink || ""}
                onChange={(v) => setField("directionLink", v)}
                placeholder={t(
                  "locationEditor.common.mapLinkPh",
                  "Paste a Google Maps link"
                )}
              />
            </EditorGroup>
          </div>
        </EditorGroup>
      ) : null}

      {tplKey === "template2" ? (
        <EditorGroup
          title={t("locationEditor.t2.title", "Branches")}
          subtitle={t(
            "locationEditor.t2.help",
            "Add branches, then select one branch to edit"
          )}
          toggleLabel={t("locationEditor.t2.toggle", "Toggle")}
          defaultOpen
        >
          <div className="space-y-4">
            <EditorGroup
              title={t("locationEditor.t2.advancedTitle", "Advanced")}
              subtitle={t(
                "locationEditor.t2.advancedHelp",
                "Google Maps API key is only needed for advanced map preview features"
              )}
              toggleLabel={t("locationEditor.common.advancedToggle", "Toggle")}
              defaultOpen={false}
            >
              <Field
                label={t(
                  "locationEditor.t2.googleMapsApiKey",
                  "Google Maps API Key (optional)"
                )}
                value={cfg.googleMapsApiKey || ""}
                onChange={(v) => setField("googleMapsApiKey", v)}
              />
              <div className="text-xs text-gray-500">
                {t(
                  "locationEditor.t2.apiHelp",
                  "If you do not know this, you can leave it empty."
                )}
              </div>
            </EditorGroup>

            <SelectCards
              items={locations}
              activeId={activeId}
              onSelect={(id) => setActiveId(id)}
              onAdd={addLoc}
              onRemove={removeLoc}
              addLabel={t("locationEditor.t2.addBranch", "Add Branch")}
              t={t}
              getTitle={(it, idx) =>
                it?.name || `${t("locationEditor.t2.branch", "Branch")} ${idx + 1}`
              }
              getSubtitle={(it) =>
                [it?.city, it?.address].filter(Boolean).join(" • ") ||
                t("locationEditor.common.noDetails", "No details yet")
              }
            />

            {!activeLoc ? (
              <div className="text-sm text-gray-600">
                {t(
                  "locationEditor.t2.selectHint",
                  "Select a branch above to edit its details."
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="text-sm font-semibold text-gray-900">
                    {t("locationEditor.t2.editing", "Editing")}{" "}
                    {activeLoc?.name ||
                      `${t("locationEditor.t2.branch", "Branch")} ${activeIndex + 1}`}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t(
                      "locationEditor.t2.editingHelp",
                      "Fill the Basic section first. The rest is optional."
                    )}
                  </div>
                </div>

                <EditorGroup
                  title={t("locationEditor.common.basic", "Basic")}
                  subtitle={t(
                    "locationEditor.common.basicHelp",
                    "Name and address"
                  )}
                  toggleLabel={t("locationEditor.common.basicToggle", "Toggle")}
                  defaultOpen
                >
                  <div className="space-y-4">
                    <Field
                      label={t("locationEditor.common.name", "Name")}
                      value={activeLoc?.name || ""}
                      onChange={(v) => updateLoc(activeIndex, { name: v })}
                      placeholder={t("locationEditor.common.namePh", "Main Branch")}
                    />
                    <Field
                      label={t("locationEditor.common.address", "Address")}
                      value={activeLoc?.address || ""}
                      onChange={(v) => updateLoc(activeIndex, { address: v })}
                    />
                    <div className="grid md:grid-cols-4 gap-4">
                      <Field
                        label={t("locationEditor.common.city", "City")}
                        value={activeLoc?.city || ""}
                        onChange={(v) => updateLoc(activeIndex, { city: v })}
                      />
                      <Field
                        label={t("locationEditor.common.state", "State")}
                        value={activeLoc?.state || ""}
                        onChange={(v) => updateLoc(activeIndex, { state: v })}
                      />
                      <Field
                        label={t("locationEditor.common.zip", "ZIP")}
                        value={activeLoc?.zip || ""}
                        onChange={(v) => updateLoc(activeIndex, { zip: v })}
                      />
                      <Field
                        label={t("locationEditor.t1.country", "Country")}
                        value={activeLoc?.country || ""}
                        onChange={(v) => updateLoc(activeIndex, { country: v })}
                      />
                    </div>
                  </div>
                </EditorGroup>

                <EditorGroup
                  title={t("locationEditor.common.contact", "Contact")}
                  subtitle={t(
                    "locationEditor.common.contactHelp",
                    "Phone, email and optional contact person"
                  )}
                  toggleLabel={t("locationEditor.common.contactToggle", "Toggle")}
                  defaultOpen={false}
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field
                      label={t("locationEditor.common.phone", "Phone")}
                      value={activeLoc?.phone || ""}
                      onChange={(v) => updateLoc(activeIndex, { phone: v })}
                    />
                    <Field
                      label={t("locationEditor.common.email", "Email")}
                      value={activeLoc?.email || ""}
                      onChange={(v) => updateLoc(activeIndex, { email: v })}
                    />
                  </div>

                  <div className="mt-4">
                    <Field
                      label={t("locationEditor.t2.manager", "Contact Person (optional)")}
                      value={activeLoc?.manager || ""}
                      onChange={(v) => updateLoc(activeIndex, { manager: v })}
                    />
                  </div>
                </EditorGroup>

                <EditorGroup
                  title={t("locationEditor.t2.hoursPerDay", "Hours")}
                  subtitle={t(
                    "locationEditor.t2.hoursHelp",
                    "Set opening hours for each day"
                  )}
                  toggleLabel={t("locationEditor.t2.hoursToggle", "Toggle")}
                  defaultOpen={false}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs text-gray-500">
                      {t(
                        "locationEditor.t2.hoursTip",
                        "Tip: fill Monday first, then copy to other days."
                      )}
                    </div>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
                      onClick={() => {
                        const monday = activeLoc?.hours?.monday || "";
                        const next = {};
                        DAYS.forEach(([k]) => {
                          next[k] = monday;
                        });
                        updateLoc(activeIndex, { hours: next });
                      }}
                    >
                      {t("locationEditor.t2.copyMonday", "Copy Monday to all")}
                    </button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    {DAYS.map(([k, label]) => (
                      <Field
                        key={k}
                        label={t(`locationEditor.days.${k}`, label)}
                        value={activeLoc?.hours?.[k] || ""}
                        onChange={(v) =>
                          updateLoc(activeIndex, {
                            hours: { ...(activeLoc?.hours || {}), [k]: v },
                          })
                        }
                        placeholder={t("locationEditor.t2.dayPh", "9:00 - 18:00")}
                      />
                    ))}
                  </div>
                </EditorGroup>

                <EditorGroup
                  title={t("locationEditor.common.map", "Map")}
                  subtitle={t(
                    "locationEditor.common.mapHelp",
                    "Directions link, embed URL, and optional coordinates"
                  )}
                  toggleLabel={t("locationEditor.common.mapToggle", "Toggle")}
                  defaultOpen={false}
                >
                  <Field
                    label={t("locationEditor.common.mapLink", "Directions Link")}
                    value={activeLoc?.mapLink || ""}
                    onChange={(v) => updateLoc(activeIndex, { mapLink: v })}
                    placeholder={t(
                      "locationEditor.common.mapLinkPh",
                      "Paste a Google Maps link"
                    )}
                  />

                  <Field
                    label={t("locationEditor.t1.mapEmbedUrl", "Google Maps Embed URL")}
                    type="textarea"
                    value={activeLoc?.mapEmbedUrl || ""}
                    onChange={(v) => updateLoc(activeIndex, { mapEmbedUrl: v })}
                  />

                  <EditorGroup
                    title={t("locationEditor.common.coords", "Coordinates (advanced)")}
                    subtitle={t(
                      "locationEditor.common.coordsHelp",
                      "Only needed for precise map features"
                    )}
                    toggleLabel={t("locationEditor.common.coordsToggle", "Toggle")}
                    defaultOpen={false}
                  >
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field
                        label={t("locationEditor.t2.coordsLat", "Latitude")}
                        value={activeLoc?.coordinates?.lat ?? ""}
                        onChange={(v) =>
                          updateLoc(activeIndex, {
                            coordinates: { ...(activeLoc?.coordinates || {}), lat: v },
                          })
                        }
                      />
                      <Field
                        label={t("locationEditor.t2.coordsLng", "Longitude")}
                        value={activeLoc?.coordinates?.lng ?? ""}
                        onChange={(v) =>
                          updateLoc(activeIndex, {
                            coordinates: { ...(activeLoc?.coordinates || {}), lng: v },
                          })
                        }
                      />
                    </div>
                  </EditorGroup>
                </EditorGroup>

                <EditorGroup
                  title={t("locationEditor.t2.features", "Highlights")}
                  subtitle={t(
                    "locationEditor.t2.featuresHelp",
                    "Add short tags like Parking, Delivery, Wheelchair access"
                  )}
                  toggleLabel={t("locationEditor.t2.featuresToggle", "Toggle")}
                  defaultOpen={false}
                >
                  <ChipsInput
                    label={t("locationEditor.t2.featuresLabel", "Features")}
                    value={activeLoc?.features || []}
                    onChange={(arr) => updateLoc(activeIndex, { features: arr })}
                    placeholder={t(
                      "locationEditor.t2.featuresPh",
                      "Type feature and press Enter"
                    )}
                    t={t}
                  />
                </EditorGroup>

                <EditorGroup
                  title={t("locationEditor.common.image", "Image")}
                  subtitle={t(
                    "locationEditor.common.imageHelp",
                    "Upload an image for this branch"
                  )}
                  toggleLabel={t("locationEditor.common.imageToggle", "Toggle")}
                  defaultOpen={false}
                >
                  {activeLoc?.image ? (
                    <img
                      src={activeLoc.image}
                      alt=""
                      className="w-full max-h-[260px] object-cover rounded-xl border border-gray-200 bg-white"
                    />
                  ) : (
                    <div className="w-full rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500 text-center">
                      {t("locationEditor.common.noImage", "No image yet")}
                    </div>
                  )}

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => updateLoc(activeIndex, { image: "" })}
                      className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
                    >
                      {t("locationEditor.common.removeImage", "Remove image")}
                    </button>
                  </div>

                  <HeroImageUploader
                    kind="location"
                    uploadImage={uploadImage}
                    onUploaded={(url) => updateLoc(activeIndex, { image: url })}
                    t={t}
                  />

                  <EditorGroup
                    title={t("locationEditor.common.advanced", "Advanced")}
                    subtitle={t(
                      "locationEditor.common.advancedHelp",
                      "Paste an image URL instead of uploading"
                    )}
                    toggleLabel={t("locationEditor.common.advancedToggle", "Toggle")}
                    defaultOpen={false}
                  >
                    <Field
                      label={t("locationEditor.common.imageUrl", "Image URL")}
                      type="url"
                      value={activeLoc?.image || ""}
                      onChange={(v) => updateLoc(activeIndex, { image: v })}
                      placeholder={t(
                        "locationEditor.common.imageUrlPlaceholder",
                        "Paste image URL"
                      )}
                    />
                  </EditorGroup>
                </EditorGroup>
              </div>
            )}
          </div>
        </EditorGroup>
      ) : null}

      {tplKey === "template3" ? (
        <EditorGroup
          title={t("locationEditor.t3.title", "Locations")}
          subtitle={t(
            "locationEditor.t3.help",
            "Add locations, then select one location to edit"
          )}
          toggleLabel={t("locationEditor.t3.toggle", "Toggle")}
          defaultOpen
        >
          <div className="space-y-4">
            <EditorGroup
              title={t("locationEditor.t3.advancedTitle", "Advanced")}
              subtitle={t(
                "locationEditor.t3.advancedHelp",
                "Google Maps API key is only needed for advanced map preview features"
              )}
              toggleLabel={t("locationEditor.common.advancedToggle", "Toggle")}
              defaultOpen={false}
            >
              <Field
                label={t(
                  "locationEditor.t3.googleMapsApiKey",
                  "Google Maps API Key (optional)"
                )}
                value={cfg.googleMapsApiKey || ""}
                onChange={(v) => setField("googleMapsApiKey", v)}
              />
            </EditorGroup>

            <SelectCards
              items={locations}
              activeId={activeId}
              onSelect={(id) => setActiveId(id)}
              onAdd={addLoc}
              onRemove={removeLoc}
              addLabel={t("locationEditor.t3.addLocation", "Add Location")}
              t={t}
              getTitle={(it, idx) =>
                it?.name || `${t("locationEditor.t3.location", "Location")} ${idx + 1}`
              }
              getSubtitle={(it) =>
                [it?.city, it?.address].filter(Boolean).join(" • ") ||
                t("locationEditor.common.noDetails", "No details yet")
              }
            />

            {!activeLoc ? (
              <div className="text-sm text-gray-600">
                {t(
                  "locationEditor.t3.selectHint",
                  "Select a location above to edit its details."
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-2xl border border-gray-200 bg-white p-4">
                  <div className="text-sm font-semibold text-gray-900">
                    {t("locationEditor.t3.editing", "Editing")}{" "}
                    {activeLoc?.name ||
                      `${t("locationEditor.t3.location", "Location")} ${activeIndex + 1}`}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t(
                      "locationEditor.t3.editingHelp",
                      "Fill the Basic section first. The rest is optional."
                    )}
                  </div>
                </div>

                <EditorGroup
                  title={t("locationEditor.common.basic", "Basic")}
                  subtitle={t(
                    "locationEditor.common.basicHelp",
                    "Name and address"
                  )}
                  toggleLabel={t("locationEditor.common.basicToggle", "Toggle")}
                  defaultOpen
                >
                  <div className="space-y-4">
                    <Field
                      label={t("locationEditor.common.name", "Name")}
                      value={activeLoc?.name || ""}
                      onChange={(v) => updateLoc(activeIndex, { name: v })}
                    />
                    <Field
                      label={t("locationEditor.common.address", "Address")}
                      value={activeLoc?.address || ""}
                      onChange={(v) => updateLoc(activeIndex, { address: v })}
                    />

                    <div className="grid md:grid-cols-4 gap-4">
                      <Field
                        label={t("locationEditor.common.city", "City")}
                        value={activeLoc?.city || ""}
                        onChange={(v) => updateLoc(activeIndex, { city: v })}
                      />
                      <Field
                        label={t("locationEditor.common.state", "State")}
                        value={activeLoc?.state || ""}
                        onChange={(v) => updateLoc(activeIndex, { state: v })}
                      />
                      <Field
                        label={t("locationEditor.common.zip", "ZIP")}
                        value={activeLoc?.zip || ""}
                        onChange={(v) => updateLoc(activeIndex, { zip: v })}
                      />
                      <Field
                        label={t("locationEditor.t1.country", "Country")}
                        value={activeLoc?.country || ""}
                        onChange={(v) => updateLoc(activeIndex, { country: v })}
                      />
                    </div>
                  </div>
                </EditorGroup>

                <EditorGroup
                  title={t("locationEditor.common.contact", "Contact")}
                  subtitle={t(
                    "locationEditor.common.contactHelp",
                    "Phone and email for this location"
                  )}
                  toggleLabel={t("locationEditor.common.contactToggle", "Toggle")}
                  defaultOpen={false}
                >
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field
                      label={t("locationEditor.common.phone", "Phone")}
                      value={activeLoc?.phone || ""}
                      onChange={(v) => updateLoc(activeIndex, { phone: v })}
                    />
                    <Field
                      label={t("locationEditor.common.email", "Email")}
                      value={activeLoc?.email || ""}
                      onChange={(v) => updateLoc(activeIndex, { email: v })}
                    />
                  </div>
                </EditorGroup>

                <EditorGroup
                  title={t("locationEditor.t3.details", "Details")}
                  subtitle={t(
                    "locationEditor.t3.detailsHelp",
                    "Hours, map and image"
                  )}
                  toggleLabel={t("locationEditor.t3.detailsToggle", "Toggle")}
                  defaultOpen={false}
                >
                  <Field
                    label={t("locationEditor.t3.hoursString", "Hours")}
                    value={activeLoc?.hours || ""}
                    onChange={(v) => updateLoc(activeIndex, { hours: v })}
                    placeholder={t(
                      "locationEditor.t3.hoursPh",
                      "Mon-Fri: 9am-6pm"
                    )}
                  />

                  <Field
                    label={t("locationEditor.common.mapLink", "Directions Link")}
                    value={activeLoc?.mapLink || ""}
                    onChange={(v) => updateLoc(activeIndex, { mapLink: v })}
                    placeholder={t(
                      "locationEditor.common.mapLinkPh",
                      "Paste a Google Maps link"
                    )}
                  />

                  <Field
                    label={t("locationEditor.t1.mapEmbedUrl", "Google Maps Embed URL")}
                    type="textarea"
                    value={activeLoc?.mapEmbedUrl || ""}
                    onChange={(v) => updateLoc(activeIndex, { mapEmbedUrl: v })}
                  />

                  <EditorGroup
                    title={t("locationEditor.common.coords", "Coordinates (advanced)")}
                    subtitle={t(
                      "locationEditor.common.coordsHelp",
                      "Only needed for precise map features"
                    )}
                    toggleLabel={t("locationEditor.common.coordsToggle", "Toggle")}
                    defaultOpen={false}
                  >
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field
                        label={t("locationEditor.t2.coordsLat", "Latitude")}
                        value={activeLoc?.coordinates?.lat ?? ""}
                        onChange={(v) =>
                          updateLoc(activeIndex, {
                            coordinates: { ...(activeLoc?.coordinates || {}), lat: v },
                          })
                        }
                      />
                      <Field
                        label={t("locationEditor.t2.coordsLng", "Longitude")}
                        value={activeLoc?.coordinates?.lng ?? ""}
                        onChange={(v) =>
                          updateLoc(activeIndex, {
                            coordinates: { ...(activeLoc?.coordinates || {}), lng: v },
                          })
                        }
                      />
                    </div>
                  </EditorGroup>
                </EditorGroup>

                <EditorGroup
                  title={t("locationEditor.common.image", "Image")}
                  subtitle={t(
                    "locationEditor.common.imageHelp",
                    "Upload an image for this location"
                  )}
                  toggleLabel={t("locationEditor.common.imageToggle", "Toggle")}
                  defaultOpen={false}
                >
                  {activeLoc?.image ? (
                    <img
                      src={activeLoc.image}
                      alt=""
                      className="w-full max-h-[260px] object-cover rounded-xl border border-gray-200 bg-white"
                    />
                  ) : (
                    <div className="w-full rounded-xl border border-dashed border-gray-300 bg-white px-4 py-6 text-sm text-gray-500 text-center">
                      {t("locationEditor.common.noImage", "No image yet")}
                    </div>
                  )}

                  <div className="flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => updateLoc(activeIndex, { image: "" })}
                      className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-white"
                    >
                      {t("locationEditor.common.removeImage", "Remove image")}
                    </button>
                  </div>

                  <HeroImageUploader
                    kind="location"
                    uploadImage={uploadImage}
                    onUploaded={(url) => updateLoc(activeIndex, { image: url })}
                    t={t}
                  />

                  <EditorGroup
                    title={t("locationEditor.common.advanced", "Advanced")}
                    subtitle={t(
                      "locationEditor.common.advancedHelp",
                      "Paste an image URL instead of uploading"
                    )}
                    toggleLabel={t("locationEditor.common.advancedToggle", "Toggle")}
                    defaultOpen={false}
                  >
                    <Field
                      label={t("locationEditor.common.imageUrl", "Image URL")}
                      type="url"
                      value={activeLoc?.image || ""}
                      onChange={(v) => updateLoc(activeIndex, { image: v })}
                    />
                  </EditorGroup>
                </EditorGroup>
              </div>
            )}
          </div>
        </EditorGroup>
      ) : null}
    </div>
  );
}